import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import PageLayout from "../../PageLayout";
import AllProjectList from "./AllProjectList";
import { getAllProject,getAllProjectComplete, updateProjectStatusComment } from "../../../store/actions/ProjectActions";
import UnAuthorizedComponent from "../../UnAuthorized/UnAuthorizedComponent";
import { isRoleMatch } from "../../../utils";
import LoadingOverlay from "react-loading-overlay-ts";
import { useUnsavedChangesWarning } from "../../common/NavigationPrompt";
import { saveProjectsFromDashboard } from "../../../apis/projectPlanApi";
import { Toast } from "primereact/toast";

function AllProjects() {
  const User = useSelector((state) => state?.UserReducer);
  const userInformation = User?.userInformation;
  const dispatch = useDispatch();
  const user_group = useSelector((state) => state?.UserDetailsReducer?.userDetails?.UserGroup);
  // 3109 security matrix
  const [checkReadWriteAccess, setUserAccess] = useState(false);
  const [loader, setLoader] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoredProjects, setRestoredProjects] = useState(new Set());
  const [hasComments, setHasComments] = useState(true);
  const [isRestoring, setIsRestoring] = useState(false);
  const [initialComments, setInitialComments] = useState(new Map());
  const { markDirty, markClean } = useUnsavedChangesWarning();
  const toast = useRef(null);
  const enableSaveButton = () => setHasComments(true);
  const [projectWithStatus, setProjectWithStatus] = useState(true);

  const {
    userDetails: { AccessGroup },
  } = useSelector((state) => state?.UserDetailsReducer);

  // Get the all projects data from Redux store
  const allProjectsData = useSelector((state) => state?.myProject?.allProjects);

  // Function to check if there are any comments entered
  const checkForComments = () => {
    if (!allProjectsData || allProjectsData.length === 0) {
      return false;
    }
    
    return allProjectsData.some(project => 
      project.ProjectStatusComment && 
      project.ProjectStatusComment.toString().trim() !== ""
    );
  };

  // Monitor changes in allProjectsData to update button state
  useEffect(() => {
    if (!isRestoring && allProjectsData && initialComments.size > 0) {
      let hasChanges = false;
      
      for (const project of allProjectsData) {
        const currentComment = project.ProjectStatusComment || '';
        const initialComment = initialComments.get(project.Project_ID) || '';
        
        // Check if current comment is different from initial AND not empty
        if (currentComment !== initialComment && currentComment.trim() !== '') {
          hasChanges = true;
          break;
        }
      }
      
      setHasComments(hasChanges);
    }
  }, [allProjectsData, isRestoring, initialComments]);

  useEffect(()=>{
    markClean();
    // writing this line because on loading the pages we need to clear unsaveChanges localstorage
  },[])

  // Initialize state on mount
  useEffect(() => {
    setHasComments(false);
    setIsRestoring(false);
  }, []);

  useEffect(() => {
    //load the api only when user has access as per 3109 security matrix
    if (user_group?.length && userInformation?.userid && checkReadWriteAccess) {
      const payLoad = {
        ArtworkAgilityPage: {
          UserGroup: user_group,
          PM: userInformation?.userid,
        },
      };
      if( projectWithStatus ){
        dispatch(getAllProject(payLoad));
      }else{
        dispatch(getAllProjectComplete(payLoad));
      }  
      setLoader(false);
    } else {
      setLoader(false);
    }
  }, [dispatch, user_group?.length, userInformation?.userid, checkReadWriteAccess,projectWithStatus]);

  // Restore comments from localStorage after data is loaded
  // useEffect(() => {
  //   if (allProjectsData && allProjectsData.length > 0 && !isRestoring && initialComments.size === 0) {
  //     setIsRestoring(true);
      
  //     // Store initial comments before restoration
  //     const currentComments = new Map();
  //     allProjectsData.forEach(project => {
  //       currentComments.set(project.Project_ID, project.ProjectStatusComment || '');
  //     });
  //     setInitialComments(currentComments);
      
  //     // Add a small delay to ensure all other data loading is complete
  //     const timer = setTimeout(() => {
  //       const savedComments = localStorage.getItem('projectComments');
  //       if (savedComments) {
  //         const commentsObj = JSON.parse(savedComments);Object.keys(commentsObj).forEach(projectId => {
  //           const project = allProjectsData.find(p => p.Project_ID === projectId);
  //           if (project && commentsObj[projectId] && commentsObj[projectId].trim() !== '') {
  //             const currentComment = project.ProjectStatusComment || '';
  //             const savedComment = commentsObj[projectId];
            
  //             if (!restoredProjects.has(projectId) && currentComment !== savedComment) {
  //               dispatch(updateProjectStatusComment(projectId, savedComment));
  //               setRestoredProjects(prev => new Set([...prev, projectId]));
  //             }
  //           }
  //         });
  //       }
        
  //       setTimeout(() => {
  //         setIsRestoring(false);
  //       }, 50); // Short delay to ensure restoration dispatch is complete
  //     }, 100); // Small delay to ensure other effects have run

  //     return () => clearTimeout(timer);
  //   }
  // }, [allProjectsData, dispatch, restoredProjects, isRestoring, initialComments]);
  // 3109 security matrix
  useEffect(() => {
    const isUserAccess = isRoleMatch(AccessGroup, [
      "CapacityManager",
      "ProjectManager",
    ]);
    setUserAccess(isUserAccess);
  }, [AccessGroup]);
// useEffect(()=>{
//    const existingComments = localStorage.getItem('projectComments');
//         console.log("existingComments",existingComments)
// })
  const handleSave = async () => {
    if (!allProjectsData || allProjectsData.length === 0) {
      return;
    }
  setSaving(true);
    
    try {
      // Check for comments that exceed 256 characters
      const invalidComments = allProjectsData?.filter(project => {
        const comment = project.ProjectStatusComment && project.ProjectStatusComment.toString().trim();
        return comment && comment.length > 256;
      });

      if (invalidComments && invalidComments.length > 0) {
        toast.current.show({
          severity: 'warn',
          summary: 'Invalid Comments',
          detail: `${invalidComments.length} project(s) have comments exceeding 256 characters. Please shorten them before saving.`,
          life: 5000
        });
        setSaving(false);
        return;
      }

      const projectsToSave = allProjectsData?.filter(project => {
        const comment = project.ProjectStatusComment && project.ProjectStatusComment.toString().trim();
        return comment !== "" && comment.length <= 256;
      });

      if (!projectsToSave || projectsToSave.length === 0) {
        toast.current.show({
          severity: 'info',
          summary: 'No Changes',
          detail: 'No project status comments to save.',
          life: 3000
        });
        setSaving(false);
        return;
      }

      // Call the same API endpoint as MyProjects with matching format
      const formData = {
        ArtworkAgilityProjects: projectsToSave.map(project => ({
          AWMProjectID: project.Project_ID, // Using AWMProjectID to match My Projects format
          ProjectStatusComment: project.ProjectStatusComment
        }))
      };
      
      const response = await saveProjectsFromDashboard(formData);
    if (response?.status === 200) {
        // Store comments in localStorage for persistence across page navigation
        const commentsToStore = {};
        projectsToSave.forEach(project => {
          commentsToStore[project.Project_ID] = project.ProjectStatusComment;
        });
        const existingComments = localStorage.getItem('projectComments');
        
        const allComments = existingComments ? JSON.parse(existingComments) : {};
        Object.assign(allComments, commentsToStore);
        localStorage.setItem('projectComments', JSON.stringify(allComments));
        
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Project status comments saved successfully!',
          life: 3000
        });
        
        markClean();
        setHasComments(false); // Reset button state after successful save
      } else {
        throw new Error('Save operation failed');
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save project status comments. Please try again.',
        life: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (allProjectsData && allProjectsData.length > 0) {
      allProjectsData.forEach(project => {
        if (project.ProjectStatusComment) {
          dispatch(updateProjectStatusComment(project.Project_ID, ''));
        }
      });
    }
    
    // Clear any comments from localStorage for current projects


    
    // Reset the restored projects set
  
    
    // Reload the page data to reset any unsaved changes
  
    
   
  };
  return (
    <>
      <PageLayout>
        <Toast ref={toast} />
        <div className="content-layout" id="tableDiv">
          <div className="tabular-view">
            {
              /* - lets wait for to Load all the data */
              loader ? (
                <LoadingOverlay active={loader} spinner text="" />
              ) : /* 3109- Verify whether the user has access to this page*/
              checkReadWriteAccess ? (
                <AllProjectList 
                  header="All Projects" 
                  enableSaveButton={enableSaveButton} 
                  projectWithStatus={projectWithStatus}
                  setProjectWithStatus={setProjectWithStatus}/>
              ) : (
                AccessGroup && AccessGroup.length && <UnAuthorizedComponent nowrapper={true} />
              )
            }
          </div>
          {/* <div className="form-buttons">
            <button 
              type="button"
              className="button-layout saveBtn" 
              onClick={handleSave}
              disabled={saving || !hasComments}
            > Save
            </button>
            <button 
            type="button"
              className="button-layout cancelBtn"
              onClick={handleCancel}
              disabled={!hasComments}
            >
              Cancel
            </button>
          </div> */}
        </div>
      </PageLayout>
    </>
  );
}

export default AllProjects;
